/**
 * @voltxampere/presets — Father's reactive-power compensation cap
 * bank controller (Sprint 54.3-1, VISION §10 canonical regression
 * devresi).
 *
 * Şefim'in babasının 50+ component kondansatör bankı kontrol
 * devresinin proxy schematic'i (Sprint 54.1
 * docs/sprints/SPRINT_54_FATHERS_CIRCUIT_SCHEMATIC.md). Orijinal
 * şematik fiziksel kayıp; Türkiye sanayi paneli pratiği (reactive
 * power compensation switching) reverse-engineer'ı ile türetildi.
 *
 * Topology: AC mains → voltage sense divider → bridge rectifier →
 * DC filter → LM393 comparator (vs Vref) → BC547 driver → MOC3041
 * zero-cross optocoupler → BT136 TRIAC → capacitor bank.
 *
 * Component count: ~50 (target 52 per proxy doc §4). Mevcut engine
 * stamps:
 *
 *   - Voltage sources (V_mains AC, V_aux DC)
 *   - B-source (Sprint 54.2b-5) for PWM control proxy
 *   - 4× 1N4148 sense bridge diodes
 *   - Resistor network (sense + filter + divider + gate + snubber +
 *     10× bleed for cap bank)
 *   - 10× 5µF AC capacitor bank + 1 filter + 1 snubber = 12 caps
 *   - LM393 (Sprint 54.2a open-collector comparator)
 *   - BC547 NPN (Sprint 7 Ebers-Moll)
 *   - MOC3041 (Sprint 54.2b-2 zero-cross optocoupler)
 *   - BT136 (Sprint 54.2b-1 TRIAC bidirectional latch)
 *
 * SCOPE NOTE: CT (current transformer) replaced with simplified
 * voltage proxy — engine doesn't yet model mutual inductance (K
 * coupling, Sprint 55+). The sense-burden node is fed from a high-
 * impedance divider off the main rail.
 *
 * Wires intentionally empty — DEVRELER thumbnail uses custom SVG
 * (Sprint 18 B5 pattern); canvas layout TBD (Sprint 54.4 polish).
 *
 * Copyright © 2026 360 Studio
 * Licensed under MIT (see LICENSE file in repo root)
 */
import { makeCircuit, makeNet, makeComponent } from "../_builders.js";
export const fathersCapBankController = {
    id: "fathers-cap-bank-controller",
    name: {
        tr: "Babanın Kondansatör Bankı Kontrolörü",
        en: "Father's Capacitor Bank Controller",
    },
    category: "power",
    difficulty: 5,
    description: {
        tr: "VISION §10 referans devresi (proxy). Reaktif güç kompanzasyonu için " +
            "AC mains üzerinden anahtarlanan 50µF kondansatör bankı; LM393 + BC547 " +
            "+ MOC3041 + BT136 kontrol zinciri. ~50 component, engine extension #5 " +
            "regression case.",
        en: "VISION §10 reference circuit (proxy). 50µF capacitor bank switched " +
            "on AC mains for reactive-power compensation; LM393 + BC547 + MOC3041 " +
            "+ BT136 control chain. ~50 components, engine extension #5 regression " +
            "case.",
    },
    circuit: makeCircuit({
        slot: 1,
        name: "Father's Cap Bank Controller",
        description: "Reactive power compensation switching (VISION §10 proxy)",
        nets: [
            makeNet({ name: "gnd", isGround: true }),
            // AC mains.
            makeNet({ name: "L" }), // phase line
            // Auxiliary DC rail.
            makeNet({ name: "AUX" }),
            // Sense conditioning.
            makeNet({ name: "V_SENSE" }),
            makeNet({ name: "RECT_P" }),
            // Sprint 54.3d-1 — diamond-bridge negative rail. Held near GND
            // by D3 in steady state; floats slightly below 0 during the
            // negative half-cycle so D4 can return current from V_SENSE
            // through C1_filter. Necessary for full-wave rectification of
            // a single-ended V_SENSE input (CT-derived AC2 not yet
            // available — Sprint 55+ K-coupling backlog).
            makeNet({ name: "RECT_N" }),
            makeNet({ name: "VDC" }),
            makeNet({ name: "VREF" }),
            // Control chain.
            makeNet({ name: "COMP_OUT" }),
            makeNet({ name: "Q1_BASE" }),
            makeNet({ name: "Q1_COLL" }),
            makeNet({ name: "GATE" }),
            makeNet({ name: "TRIAC_T1" }),
            // Power switching.
            makeNet({ name: "CAP_BANK" }),
            // B-source PWM output (control override, separate rail).
            makeNet({ name: "PWM_OUT" }),
        ],
        components: [
            // ── Sources ─────────────────────────────────────────────────────
            // V_mains — AC 311V peak, 50Hz proxy. Using voltage-source DC
            // value (peak) for DC OP; transient analysis would use ac-source
            // or pulse-source for true sinusoid. MVP DC-equivalent.
            makeComponent({
                id: "V_mains",
                type: "voltage-source",
                params: { voltage: 220 }, // RMS proxy at DC OP
                terminals: { pos: "net_L", neg: "net_gnd" },
                label: "V_mains",
                position: { x: -400, y: 0 },
                rotation: 0,
            }),
            // V_aux — DC 5V rail for LM393 + BC547 + MOC LED.
            makeComponent({
                id: "V_aux",
                type: "voltage-source",
                params: { voltage: 5 },
                terminals: { pos: "net_AUX", neg: "net_gnd" },
                label: "V_aux",
                position: { x: -400, y: 100 },
                rotation: 0,
            }),
            // B_ctrl — PWM control B-source. Expression uses net IDs
            // (Sprint 54.3 MVP: stamp doesn't have access to circuit's
            // name→id map; Sprint 55+ may extend DCStampContext to inject
            // it). For now V(net_VDC) syntax with id prefix.
            makeComponent({
                id: "B_ctrl",
                type: "b-source",
                params: {
                    expression: "(V(net_VDC) > V(net_VREF)) ? 5 : 0",
                    expressionType: "voltage",
                },
                terminals: { pos: "net_PWM_OUT", neg: "net_gnd" },
                label: "B_ctrl",
                position: { x: -400, y: 200 },
                rotation: 0,
            }),
            // ── Sense conditioning ─────────────────────────────────────────
            // Voltage divider from L to V_SENSE (Sprint 54.3e-4: 100kΩ + 10kΩ).
            //
            // Sprint 54.3a-c shipped 1MΩ + 10kΩ (scale 1/101 → V_SENSE peak
            // ±3.08V, VDC ~1.475V DC / ~2.25V transient peak). With VREF=2.5V
            // the LM393 comparator never toggled (VDC stays below VREF the
            // entire run), so the BC547→MOC→BT136 control chain could not
            // initiate and the state machines (54.3e-1/2) had no effective
            // input. Sprint 54.3e-4 (Şefim option B, in-session decision
            // 2026-05-11) tightens the divider to 100kΩ + 10kΩ (scale 1/11
            // → V_SENSE peak ±30.8V). VDC steady-state should now exceed
            // VREF and the LM393 trigger chain fires.
            makeComponent({
                id: "R_sense_top",
                type: "resistor",
                params: { resistance: 100e3 },
                terminals: { p1: "net_L", p2: "net_V_SENSE" },
                label: "R1",
                position: { x: -300, y: 0 },
                rotation: 0,
            }),
            makeComponent({
                id: "R_sense_bot",
                type: "resistor",
                params: { resistance: 10e3 },
                terminals: { p1: "net_V_SENSE", p2: "net_gnd" },
                label: "R3",
                position: { x: -250, y: 50 },
                rotation: 90,
            }),
            // ── Sense bridge rectifier (4× 1N4148, diamond, Sprint 54.3d) ──
            // Full-wave diamond bridge rectifying V_SENSE against GND:
            //   D1: V_SENSE → RECT_P   (positive half-cycle conducts to DC+)
            //   D2: GND     → RECT_P   (negative half-cycle conducts to DC+)
            //   D3: GND     → RECT_N   (negative half-cycle returns through DC-)
            //   D4: RECT_N  → V_SENSE  (positive half-cycle returns to V_SENSE)
            // 54.3c shipped this as four parallel pairs (D1≡D2, D3≡D4) — a
            // functional half-wave that pegged VDC at 0.523 V. Sprint 54.3d
            // restores the diamond. CT-derived AC2 still not available
            // (Sprint 55+ K-coupling backlog), so the bridge sees only
            // V_SENSE vs GND — this is full-wave for the SINGLE-ENDED
            // input the preset has, not the differential-AC1/AC2 bridge
            // the proxy doc §3 shows for the canonical version.
            makeComponent({
                id: "D1",
                type: "diode",
                params: { saturationCurrent: 2.52e-9, ideality: 1.752 },
                terminals: { anode: "net_V_SENSE", cathode: "net_RECT_P" },
                label: "D1",
                position: { x: -200, y: 0 },
                rotation: 0,
            }),
            makeComponent({
                id: "D2",
                type: "diode",
                params: { saturationCurrent: 2.52e-9, ideality: 1.752 },
                terminals: { anode: "net_gnd", cathode: "net_RECT_P" },
                label: "D2",
                position: { x: -200, y: 50 },
                rotation: 0,
            }),
            makeComponent({
                id: "D3",
                type: "diode",
                params: { saturationCurrent: 2.52e-9, ideality: 1.752 },
                terminals: { anode: "net_gnd", cathode: "net_RECT_N" },
                label: "D3",
                position: { x: -200, y: 100 },
                rotation: 0,
            }),
            makeComponent({
                id: "D4",
                type: "diode",
                params: { saturationCurrent: 2.52e-9, ideality: 1.752 },
                terminals: { anode: "net_RECT_N", cathode: "net_V_SENSE" },
                label: "D4",
                position: { x: -200, y: 150 },
                rotation: 0,
            }),
            // Filter capacitor across the bridge output (RECT_P ↔ RECT_N).
            makeComponent({
                id: "C1_filter",
                type: "capacitor",
                params: { capacitance: 10e-6 },
                terminals: { p1: "net_RECT_P", p2: "net_RECT_N" },
                label: "C1",
                position: { x: -150, y: 50 },
                rotation: 90,
            }),
            // RECT_N bleed to GND — gives the solver a numerical anchor
            // for the DC OP (under DC both D3 and D4 are reverse-biased,
            // C1_filter is open, leaving RECT_N otherwise floating →
            // singular matrix). 10 MΩ × 10 µF = 100 s time constant —
            // five decades above the 20 ms AC period, so the bleed has
            // negligible effect on the bridge waveform but mirrors the
            // physical leakage of a real bridge built from discrete diodes.
            // ngspice's default gmin (1 pS) gives the same anchor implicitly;
            // we make it explicit so the vxa-engine MNA matches.
            makeComponent({
                id: "R_rect_n_bleed",
                type: "resistor",
                params: { resistance: 10e6 },
                terminals: { p1: "net_RECT_N", p2: "net_gnd" },
                label: "Rb_RN",
                position: { x: -180, y: 200 },
                rotation: 90,
            }),
            // Pull-down to VDC (alias to RECT_P).
            makeComponent({
                id: "R_pulldown",
                type: "resistor",
                params: { resistance: 10e3 },
                terminals: { p1: "net_RECT_P", p2: "net_VDC" },
                label: "R4",
                position: { x: -130, y: 0 },
                rotation: 0,
            }),
            makeComponent({
                id: "R_vdc_gnd",
                type: "resistor",
                params: { resistance: 100e3 },
                terminals: { p1: "net_VDC", p2: "net_gnd" },
                label: "R4b",
                position: { x: -130, y: 50 },
                rotation: 90,
            }),
            // ── Vref divider (5V from V_aux through 4.7k/4.7k → 2.5V) ──────
            makeComponent({
                id: "R5_vref_top",
                type: "resistor",
                params: { resistance: 4.7e3 },
                terminals: { p1: "net_AUX", p2: "net_VREF" },
                label: "R5",
                position: { x: -100, y: 100 },
                rotation: 0,
            }),
            makeComponent({
                id: "R6_vref_bot",
                type: "resistor",
                params: { resistance: 4.7e3 },
                terminals: { p1: "net_VREF", p2: "net_gnd" },
                label: "R6",
                position: { x: -100, y: 150 },
                rotation: 0,
            }),
            // ── LM393 comparator (open-collector, no Schmitt — external R7) ─
            makeComponent({
                id: "U1_LM393",
                type: "opamp",
                params: {
                    openLoopGain: 2e5,
                    saturationVoltage: 0.4,
                    outputMode: "open-collector",
                    outputLowVoltage: 0.4,
                    hysteresis: false,
                },
                terminals: {
                    in_pos: "net_VDC",
                    in_neg: "net_VREF",
                    out: "net_COMP_OUT",
                },
                label: "U1",
                position: { x: 0, y: 100 },
                rotation: 0,
            }),
            // R7 hysteresis feedback (COMP_OUT → V_SENSE positive feedback).
            makeComponent({
                id: "R7_hysteresis",
                type: "resistor",
                params: { resistance: 1e6 },
                terminals: { p1: "net_COMP_OUT", p2: "net_V_SENSE" },
                label: "R7",
                position: { x: 50, y: 50 },
                rotation: 0,
            }),
            // R_pullup for COMP_OUT open-collector → V_aux.
            makeComponent({
                id: "R_comp_pull",
                type: "resistor",
                params: { resistance: 10e3 },
                terminals: { p1: "net_AUX", p2: "net_COMP_OUT" },
                label: "R_compP",
                position: { x: 50, y: 100 },
                rotation: 0,
            }),
            // ── BC547 trigger transistor ────────────────────────────────────
            makeComponent({
                id: "R8_base",
                type: "resistor",
                params: { resistance: 4.7e3 },
                terminals: { p1: "net_COMP_OUT", p2: "net_Q1_BASE" },
                label: "R8",
                position: { x: 100, y: 100 },
                rotation: 0,
            }),
            makeComponent({
                id: "Q1_BC547",
                type: "bjt-npn",
                params: { Is: 1e-14, Bf: 300, Vaf: 80, Nf: 1, Nr: 1 },
                terminals: {
                    collector: "net_Q1_COLL",
                    base: "net_Q1_BASE",
                    emitter: "net_gnd",
                },
                label: "Q1",
                position: { x: 150, y: 100 },
                rotation: 0,
            }),
            // R9 MOC LED current limit (V_aux → MOC anode = Q1 collector).
            makeComponent({
                id: "R9_led_limit",
                type: "resistor",
                params: { resistance: 330 },
                terminals: { p1: "net_AUX", p2: "net_Q1_COLL" },
                label: "R9",
                position: { x: 100, y: 50 },
                rotation: 0,
            }),
            // ── MOC3041 zero-cross optocoupler ──────────────────────────────
            // LED side: anode = Q1_COLL, cathode = gnd
            // Photo side: MT1 = GATE, MT2 = TRIAC_T1
            makeComponent({
                id: "U2_MOC3041",
                type: "optocoupler-triac",
                params: {
                    rOn: 5.0,
                    gOff: 1e-9,
                    vOn: 3.0,
                    vFLed: 1.3,
                    gLed: 0.01,
                    iFTrig: 0.005,
                    vZc: 20.0,
                    vHoldProxy: 1.0,
                },
                terminals: {
                    anode: "net_Q1_COLL",
                    cathode: "net_gnd",
                    mt1: "net_GATE",
                    mt2: "net_TRIAC_T1",
                },
                label: "U2",
                position: { x: 200, y: 100 },
                rotation: 0,
            }),
            // ── TRIAC gate resistors ────────────────────────────────────────
            makeComponent({
                id: "R10_triac_gate",
                type: "resistor",
                params: { resistance: 360 },
                terminals: { p1: "net_GATE", p2: "net_TRIAC_T1" },
                label: "R10",
                position: { x: 250, y: 100 },
                rotation: 0,
            }),
            makeComponent({
                id: "R11_triac_protect",
                type: "resistor",
                params: { resistance: 360 },
                terminals: { p1: "net_TRIAC_T1", p2: "net_L" },
                label: "R11",
                position: { x: 280, y: 100 },
                rotation: 0,
            }),
            // ── BT136 TRIAC main switch (MT1 = L, MT2 = CAP_BANK, gate = TRIAC_T1) ─
            makeComponent({
                id: "Q2_BT136",
                type: "triac",
                params: {
                    rOn: 0.5,
                    gOff: 1e-9,
                    vOn: 1.5,
                    vGateTrigger: 1.0,
                    vHoldProxy: 1.0,
                },
                terminals: {
                    mt1: "net_L",
                    mt2: "net_CAP_BANK",
                    gate: "net_TRIAC_T1",
                },
                label: "Q2",
                position: { x: 350, y: 50 },
                rotation: 0,
            }),
            // ── Snubber RC across TRIAC ─────────────────────────────────────
            makeComponent({
                id: "R12_snubber",
                type: "resistor",
                params: { resistance: 47 },
                terminals: { p1: "net_L", p2: "net_CAP_BANK" },
                label: "R12",
                position: { x: 350, y: 100 },
                rotation: 0,
            }),
            makeComponent({
                id: "C2_snubber",
                type: "capacitor",
                params: { capacitance: 100e-9 },
                terminals: { p1: "net_L", p2: "net_CAP_BANK" },
                label: "C2",
                position: { x: 350, y: 150 },
                rotation: 0,
            }),
            // ── Capacitor bank (10× 5µF AC + 10× bleed 1MΩ) ─────────────────
            makeComponent({
                id: "C3_bank",
                type: "capacitor",
                params: { capacitance: 5e-6 },
                terminals: { p1: "net_CAP_BANK", p2: "net_gnd" },
                label: "C3",
                position: { x: 400, y: 0 },
                rotation: 90,
            }),
            makeComponent({
                id: "C4_bank",
                type: "capacitor",
                params: { capacitance: 5e-6 },
                terminals: { p1: "net_CAP_BANK", p2: "net_gnd" },
                label: "C4",
                position: { x: 420, y: 0 },
                rotation: 90,
            }),
            makeComponent({
                id: "C5_bank",
                type: "capacitor",
                params: { capacitance: 5e-6 },
                terminals: { p1: "net_CAP_BANK", p2: "net_gnd" },
                label: "C5",
                position: { x: 440, y: 0 },
                rotation: 90,
            }),
            makeComponent({
                id: "C6_bank",
                type: "capacitor",
                params: { capacitance: 5e-6 },
                terminals: { p1: "net_CAP_BANK", p2: "net_gnd" },
                label: "C6",
                position: { x: 460, y: 0 },
                rotation: 90,
            }),
            makeComponent({
                id: "C7_bank",
                type: "capacitor",
                params: { capacitance: 5e-6 },
                terminals: { p1: "net_CAP_BANK", p2: "net_gnd" },
                label: "C7",
                position: { x: 480, y: 0 },
                rotation: 90,
            }),
            makeComponent({
                id: "C8_bank",
                type: "capacitor",
                params: { capacitance: 5e-6 },
                terminals: { p1: "net_CAP_BANK", p2: "net_gnd" },
                label: "C8",
                position: { x: 500, y: 0 },
                rotation: 90,
            }),
            makeComponent({
                id: "C9_bank",
                type: "capacitor",
                params: { capacitance: 5e-6 },
                terminals: { p1: "net_CAP_BANK", p2: "net_gnd" },
                label: "C9",
                position: { x: 520, y: 0 },
                rotation: 90,
            }),
            makeComponent({
                id: "C10_bank",
                type: "capacitor",
                params: { capacitance: 5e-6 },
                terminals: { p1: "net_CAP_BANK", p2: "net_gnd" },
                label: "C10",
                position: { x: 540, y: 0 },
                rotation: 90,
            }),
            makeComponent({
                id: "C11_bank",
                type: "capacitor",
                params: { capacitance: 5e-6 },
                terminals: { p1: "net_CAP_BANK", p2: "net_gnd" },
                label: "C11",
                position: { x: 560, y: 0 },
                rotation: 90,
            }),
            makeComponent({
                id: "C12_bank",
                type: "capacitor",
                params: { capacitance: 5e-6 },
                terminals: { p1: "net_CAP_BANK", p2: "net_gnd" },
                label: "C12",
                position: { x: 580, y: 0 },
                rotation: 90,
            }),
            // 10× bleed resistors (1MΩ across each cap).
            makeComponent({
                id: "Rb3",
                type: "resistor",
                params: { resistance: 1e6 },
                terminals: { p1: "net_CAP_BANK", p2: "net_gnd" },
                label: "Rb3",
                position: { x: 400, y: 100 },
                rotation: 90,
            }),
            makeComponent({
                id: "Rb4",
                type: "resistor",
                params: { resistance: 1e6 },
                terminals: { p1: "net_CAP_BANK", p2: "net_gnd" },
                label: "Rb4",
                position: { x: 420, y: 100 },
                rotation: 90,
            }),
            makeComponent({
                id: "Rb5",
                type: "resistor",
                params: { resistance: 1e6 },
                terminals: { p1: "net_CAP_BANK", p2: "net_gnd" },
                label: "Rb5",
                position: { x: 440, y: 100 },
                rotation: 90,
            }),
            makeComponent({
                id: "Rb6",
                type: "resistor",
                params: { resistance: 1e6 },
                terminals: { p1: "net_CAP_BANK", p2: "net_gnd" },
                label: "Rb6",
                position: { x: 460, y: 100 },
                rotation: 90,
            }),
            makeComponent({
                id: "Rb7",
                type: "resistor",
                params: { resistance: 1e6 },
                terminals: { p1: "net_CAP_BANK", p2: "net_gnd" },
                label: "Rb7",
                position: { x: 480, y: 100 },
                rotation: 90,
            }),
            makeComponent({
                id: "Rb8",
                type: "resistor",
                params: { resistance: 1e6 },
                terminals: { p1: "net_CAP_BANK", p2: "net_gnd" },
                label: "Rb8",
                position: { x: 500, y: 100 },
                rotation: 90,
            }),
            makeComponent({
                id: "Rb9",
                type: "resistor",
                params: { resistance: 1e6 },
                terminals: { p1: "net_CAP_BANK", p2: "net_gnd" },
                label: "Rb9",
                position: { x: 520, y: 100 },
                rotation: 90,
            }),
            makeComponent({
                id: "Rb10",
                type: "resistor",
                params: { resistance: 1e6 },
                terminals: { p1: "net_CAP_BANK", p2: "net_gnd" },
                label: "Rb10",
                position: { x: 540, y: 100 },
                rotation: 90,
            }),
            makeComponent({
                id: "Rb11",
                type: "resistor",
                params: { resistance: 1e6 },
                terminals: { p1: "net_CAP_BANK", p2: "net_gnd" },
                label: "Rb11",
                position: { x: 560, y: 100 },
                rotation: 90,
            }),
            makeComponent({
                id: "Rb12",
                type: "resistor",
                params: { resistance: 1e6 },
                terminals: { p1: "net_CAP_BANK", p2: "net_gnd" },
                label: "Rb12",
                position: { x: 580, y: 100 },
                rotation: 90,
            }),
            // Ground reference component.
            makeComponent({
                id: "GND_ref",
                type: "ground",
                params: {},
                terminals: { p: "net_gnd" },
                position: { x: -400, y: 250 },
                rotation: 0,
            }),
        ],
        wires: [],
    }),
    expectedValues: [
        // Sentinel: VREF voltage (passive divider, DC OP).
        {
            node: "VREF",
            analysis: "dc",
            value: 2.5,
            tolerance: 0.01,
            unit: "V",
        },
    ],
    learningGoals: [
        {
            tr: "Reaktif güç kompanzasyonu: cos-φ düşükse kondansatör bankı şebekeye anahtarlanır.",
            en: "Reactive power compensation: low cos-φ triggers cap bank switching onto mains.",
        },
        {
            tr: "Zero-crossing detection (MOC3041) dV/dt stresini azaltır.",
            en: "Zero-crossing detection (MOC3041) minimizes dV/dt stress on the TRIAC.",
        },
        {
            tr: "Engine extension #5 regression case: TRIAC + optocoupler + B-source DSL hep birlikte.",
            en: "Engine extension #5 regression case: TRIAC + optocoupler + B-source DSL together.",
        },
    ],
    references: [
        "docs/sprints/SPRINT_54_FATHERS_CIRCUIT_SCHEMATIC.md",
        "docs/adr/0087-engine-extension-5-thyristor-optocoupler-bsource.md",
        "VISION §10 (Şefim'in babasının 50+ component regression devresi)",
        "Vishay MOC3041, NXP BT136, TI BC547, TI LM393 datasheets",
    ],
};
/**
 * Sprint 54.3d-2 — transient-analysis variant of the fathers preset.
 *
 * Identical to {@link fathersCapBankController} except V_mains is an
 * `ac-source` with `amplitude=311, freq=50, phase=0, dcOffset=0`,
 * matching ngspice `V_mains L 0 SIN(0 311 50)` in
 * `parity/corpus/regression/fathers-circuit/fathers-tran.cir`.
 *
 * Sprint 54.3c PILOT exposed the source-type mismatch (DC=220V preset
 * vs SIN ngspice) as one of the four sentinel-capture blockers. The
 * 54.3d-2 engine extension #6 (ac-source applyDC now evaluates
 * `dcOffset + amplitude · sin(2π·freq·t + phase)` at `ctx.t`) makes
 * this variant produce a real sinusoid during transient. ADR-0095
 * §54.3d documents the extension.
 *
 * Why two variants instead of one shared preset:
 *   - DC pilot (Sprint 54.3b) intentionally uses V_mains DC=220V to
 *     get a pedagogically meaningful DC OP (VDC ≈ 1.475 V after the
 *     diamond bridge). Replacing V_mains with `ac-source(311,50,0)`
 *     would set DC OP V_mains = 0V → VDC = 0V → DC pilot becomes
 *     uninteresting.
 *   - Transient parity needs a sinusoidal V_mains to swing across
 *     the bridge and exercise the LM393 + BC547 + MOC + BT136 chain.
 *
 * The two presets share IDs that mirror each other; the ngspice
 * corpus has a 1:1 pairing (`fathers.cir` ↔ `fathers-cap-bank-controller`
 * and `fathers-tran.cir` ↔ `fathers-cap-bank-controller-transient`).
 */
export const fathersCapBankControllerTransient = {
    ...fathersCapBankController,
    id: "fathers-cap-bank-controller-transient",
    name: {
        tr: "Babanın Kondansatör Bankı Kontrolörü (Transient)",
        en: "Father's Capacitor Bank Controller (Transient)",
    },
    circuit: {
        ...fathersCapBankController.circuit,
        name: "Father's Cap Bank Controller (Transient)",
        description: "Sinusoidal AC mains variant — Sprint 54.3d-2 transient parity",
        components: fathersCapBankController.circuit.components.map((comp) => comp.id === "V_mains"
            ? {
                ...comp,
                type: "ac-source",
                params: { amplitude: 311, freq: 50, phase: 0, dcOffset: 0 },
            }
            : comp),
        simulationSettings: {
            ...fathersCapBankController.circuit.simulationSettings,
            // Sprint 54.3d-2 — settings authored against the ngspice
            // SIN(0 311 50) mirror in fathers-tran.cir. Engine transient
            // currently cannot converge this fixture's first step: the
            // 50 µF cap bank + 10 µF filter + diamond bridge of nonlinear
            // diodes + active devices (BC547, LM393, MOC subcircuit, BT136
            // subcircuit) combine into a stiff system where the engine's
            // adaptive-step controller exhausts h-reductions before the
            // first step accepts. Sprint 54.3e scope (engine extension
            // #7): step-retry-with-source-stepping inside the transient
            // solver + tighter gmin progression. Until then the preset
            // exists as the AUTHORITATIVE source-of-truth for the
            // ngspice ↔ engine transient parity pairing (54.3e will run
            // the comparison once convergence lands).
            transient: {
                tstart: 0,
                tstop: 100e-3, // 5 cycles @ 50 Hz (matches fathers-tran.cir)
                method: "be",
                reltol: 1e-2,
                abstol: 1e-9,
            },
        },
    },
};
//# sourceMappingURL=fathers-cap-bank-controller.js.map